import React, {useEffect, useMemo} from 'react';
import type {FCWithChildren} from 'types';
import type {CodeListId, CombinedCodeListId, LocalCodeListId,} from 'services/codelists/types';
import {CombinedCodeListIds, PartCodeLists} from 'services/codelists/types';
import {useLocalization} from 'localization';
import {isLinkedRoleCodeListId} from 'schemaDefinition';
import type {LinkedRoleCodeListId} from 'schemaDefinition/types/linkTypes';
import {AllLocalCodeListIds} from 'schemaDefinition/types/linkTypes';
import {selectSettingsPreferredLocale} from 'services/settings';
import type {SetLoadStatus} from 'services/utils/hooks';
import {useBatchedLoadStatus} from 'services/utils/hooks';
import {useAppSelector, useAppStore} from 'store/hooks';
import {SceneError} from 'components/scene/SceneError';
import {SceneLoader} from 'components/scene/SceneLoader';
import {useGetCodelistQuery} from '../codelistApi';
import {selectCodelistFromCache} from '../codelistSelectors';
import {mockCodeLists} from '../consts';

type Props = {
  codeLists: (CodeListId | LinkedRoleCodeListId)[];
};

const isLoadableFromBackend = (
  id: CodeListId | LinkedRoleCodeListId,
): id is CodeListId =>
  !isLinkedRoleCodeListId(id) &&
  !AllLocalCodeListIds.includes(id as LocalCodeListId) &&
  !mockCodeLists.includes(id as CodeListId);

/**
 * Triggers and subscribes to a single codelist via RTK Query. The subscription
 * keeps the cache entry alive while the gate is mounted so consumer hooks can
 * read it.
 */
const CodelistLoader: React.FC<{
  id: CodeListId;
  language: string;
  onStatus: SetLoadStatus;
}> = ({id, language, onStatus}) => {
  const {isError, isSuccess} = useGetCodelistQuery(
    {id, language},
    {
      refetchOnReconnect: true,
    },
  );

  useEffect(() => {
    if (isError) {
      onStatus(id, 'Failed');
    } else if (isSuccess) {
      onStatus(id, 'Loaded');
    } else {
      onStatus(id, 'Loading');
    }
  }, [id, isError, isSuccess, onStatus]);

  return null;
};

/**
 * Will only render children after code lists are successfully loaded.
 * Shows spinner while loading, and an error message if any fails.
 *
 * Combined codelists (e.g. WORK_AND_EXPRESSION_ROLE_TYPE) are not fetched
 * directly; the gate triggers loads for their part codelists and the
 * consumer-level selector composes them on read.
 */
export const CodeListsGate: FCWithChildren<Props> = ({
  codeLists: givenCodeLists,
  children,
}) => {
  const {t} = useLocalization();
  const language = useAppSelector(selectSettingsPreferredLocale);
  const store = useAppStore();

  const loadIds = useMemo<CodeListId[]>(() => {
    const expanded = new Set<CodeListId>();
    givenCodeLists.forEach(id => {
      if (CombinedCodeListIds.includes(id as CombinedCodeListId)) {
        PartCodeLists[id as CombinedCodeListId].forEach(partId => {
          if (isLoadableFromBackend(partId)) {
            expanded.add(partId);
          }
        });
      } else if (isLoadableFromBackend(id)) {
        expanded.add(id as CodeListId);
      }
    });

    return Array.from(expanded);
  }, [givenCodeLists]);

  // Only mount a sub-loader for ids that aren't already cached (e.g. by an
  // outer gate). Computed once per loadIds/language change — we don't want
  // this list to shrink mid-lifecycle, since each sub-loader holds the RTK
  // Query subscription that keeps its cache entry alive.
  const notLoadedIds = useMemo<CodeListId[]>(() => {
    const state = store.getState();
    return loadIds.filter(id => !selectCodelistFromCache(id, language, state));
  }, [loadIds, language, store]);

  const {status, setStatus} = useBatchedLoadStatus(notLoadedIds);

  return (
    <>
      {notLoadedIds.map(id => (
        <CodelistLoader
          key={id}
          id={id}
          language={language}
          onStatus={setStatus}
        />
      ))}
      {status === 'Failed' ? (
        <SceneError error={t('error.codelist.failedToLoad')} applySceneHeight />
      ) : status === 'Loading' ? (
        <SceneLoader
          loadingType={t('general.entity.codelist')}
          applySceneHeight
        />
      ) : (
        children
      )}
    </>
  );
};
